import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import './VendorChat.css';

const VendorChat = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [vendor, setVendor] = useState(null);
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  useEffect(() => {
    const vendorToken = localStorage.getItem('vendorToken');
    const vendorInfo = localStorage.getItem('vendorInfo');

    if (!vendorToken || !vendorInfo) {
      navigate('/vendor/login');
      return;
    }

    setVendor(JSON.parse(vendorInfo));
    loadConversation(vendorToken);
    initializeSocket(vendorToken);

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [conversationId, navigate]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const initializeSocket = (token) => {
    socketRef.current = io(API_URL, {
      auth: { token },
      transports: ['websocket', 'polling']
    });

    socketRef.current.on('connect', () => {
      console.log('Socket connected');
      if (conversationId) {
        socketRef.current.emit('join_conversation', conversationId);
      }
    });

    socketRef.current.on('new_message', (message) => {
      setMessages((prev) => [...prev, message]);
    });

    socketRef.current.on('user_typing', (data) => {
      console.log('User typing:', data);
    });

    socketRef.current.on('error', (error) => {
      console.error('Socket error:', error);
    });
  };

  const loadConversation = async (token) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/v1/chat/conversations/${conversationId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setConversation(response.data.conversation);
      setMessages(response.data.conversation.messages || []);
    } catch (error) {
      console.error('Error loading conversation:', error);
      if (error.response?.status === 401) {
        navigate('/vendor/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    const vendorToken = localStorage.getItem('vendorToken');

    try {
      const response = await axios.post(
        `${API_URL}/api/v1/chat/conversations/${conversationId}/messages`,
        { content: newMessage },
        {
          headers: { Authorization: `Bearer ${vendorToken}` }
        }
      );

      setMessages((prev) => [...prev, response.data.message]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleTyping = () => {
    if (socketRef.current && conversationId) {
      socketRef.current.emit('typing', {
        conversationId,
        userName: vendor?.company_name
      });
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach((message) => {
      const date = formatDate(message.created_at);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });
    return groups;
  };

  if (loading) {
    return (
      <div className="vendor-chat-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading conversation...</p>
      </div>
    );
  }

  const messageGroups = groupMessagesByDate(messages);

  return (
    <div className="vendor-chat">
      {/* Header */}
      <div className="vendor-chat-header">
        <button onClick={() => navigate('/vendor/dashboard')} className="back-button">
          <i className="fas fa-arrow-left"></i>
        </button>
        <div className="chat-info">
          <h2>{conversation?.title || 'Conversation'}</h2>
          <span className="chat-type">{conversation?.type}</span>
        </div>
        <div className="vendor-badge">
          <i className="fas fa-building"></i>
          {vendor?.company_name}
        </div>
      </div>

      {/* Messages */}
      <div className="vendor-chat-messages">
        {Object.keys(messageGroups).length === 0 ? (
          <div className="empty-messages">
            <i className="fas fa-comments"></i>
            <p>No messages yet</p>
            <span>Start the conversation by sending a message below</span>
          </div>
        ) : (
          Object.entries(messageGroups).map(([date, msgs]) => (
            <div key={date} className="message-group">
              <div className="date-divider">
                <span>{date}</span>
              </div>
              {msgs.map((message) => {
                const isVendorMessage = message.vendor_id === vendor?.id;
                return (
                  <div
                    key={message.id}
                    className={`message ${isVendorMessage ? 'sent' : 'received'}`}
                  >
                    <div className="message-content">
                      {!isVendorMessage && (
                        <div className="message-sender">
                          {message.user?.first_name} {message.user?.last_name}
                        </div>
                      )}
                      <div className="message-text">{message.content}</div>
                      <div className="message-time">{formatTime(message.created_at)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSendMessage} className="vendor-chat-input">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleTyping}
          placeholder="Type your message..."
          disabled={sending}
        />
        <button type="submit" disabled={!newMessage.trim() || sending}>
          {sending ? (
            <i className="fas fa-spinner fa-spin"></i>
          ) : (
            <i className="fas fa-paper-plane"></i>
          )}
        </button>
      </form>
    </div>
  );
};

export default VendorChat;


